import { useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { RefreshCw, Store } from 'lucide-react'
import { supabase } from '@/supabase-client'
import { useSquareSync } from '@/hooks/useSquareSync'

export default function MarketEventsList({ events, loading, onRefresh, isConnected }) {
  const navigate = useNavigate()
  const { syncEvent, syncing } = useSquareSync()
  const [syncJobs, setSyncJobs] = useState({})
  const [eventRevenue, setEventRevenue] = useState({})
  const [eventCosts, setEventCosts] = useState({})
  const [syncingEventId, setSyncingEventId] = useState(null)

  // Fetch sync job statuses and revenue for all events
  useEffect(() => {
    if (!events.length) return

    const eventIds = events.map(e => e.id)

    // Fetch sync jobs
    supabase
      .from('square_sync_jobs')
      .select('market_event_id, status, completed_at, sales_created, payout_status, payout_date, payout_fee')
      .in('market_event_id', eventIds)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        const jobMap = {}
        for (const job of data || []) {
          // Keep the latest job per event
          if (!jobMap[job.market_event_id]) {
            jobMap[job.market_event_id] = job
          }
        }
        setSyncJobs(jobMap)
      })

    // Fetch revenue per event
    supabase
      .from('sales')
      .select('market_event_id, sale_items(sub_total)')
      .in('market_event_id', eventIds)
      .then(({ data }) => {
        const revenueMap = {}
        for (const sale of data || []) {
          const eventId = sale.market_event_id
          if (!revenueMap[eventId]) revenueMap[eventId] = 0
          revenueMap[eventId] += sale.sale_items.reduce(
            (sum, item) => sum + (parseFloat(item.sub_total) || 0), 0
          )
        }
        setEventRevenue(revenueMap)
      })

    // Fetch costs per event
    supabase
      .from('market_event_costs')
      .select('market_event_id, stall_fee, staff_cost, travel_cost, other_cost')
      .in('market_event_id', eventIds)
      .then(({ data }) => {
        const costMap = {}
        for (const cost of data || []) {
          costMap[cost.market_event_id] =
            (parseFloat(cost.stall_fee) || 0) +
            (parseFloat(cost.staff_cost) || 0) +
            (parseFloat(cost.travel_cost) || 0) +
            (parseFloat(cost.other_cost) || 0)
        }
        setEventCosts(costMap)
      })
  }, [events])

  const handleSync = async (e, eventId) => {
    e.stopPropagation()
    setSyncingEventId(eventId)
    try {
      await syncEvent(eventId)
      onRefresh()
    } finally {
      setSyncingEventId(null)
    }
  }

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':')
    return `${h}:${m}`
  }

  const getSyncBadge = (eventId) => {
    const job = syncJobs[eventId]
    const revenue = eventRevenue[eventId]

    // If revenue exists, the event has been synced successfully
    if (revenue > 0) {
      return <Badge className="text-xs bg-green-100 text-green-800 hover:bg-green-100">Synced</Badge>
    }

    // Check active job status
    if (job) {
      switch (job.status) {
        case 'running':
          return <Badge className="text-xs bg-blue-100 text-blue-800 hover:bg-blue-100">Syncing...</Badge>
        case 'scheduled':
          return <Badge className="text-xs bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Scheduled</Badge>
        case 'failed':
          return <Badge variant="destructive" className="text-xs">Failed</Badge>
        case 'completed':
          return <Badge className="text-xs bg-green-100 text-green-800 hover:bg-green-100">No orders</Badge>
        default:
          break
      }
    }

    return <Badge variant="outline" className="text-xs">Not synced</Badge>
  }

  const today = new Date().toISOString().split('T')[0]
  const upcoming = events.filter(e => e.date >= today)
  const past = events.filter(e => e.date < today)

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <RefreshCw className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (events.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Store className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground">No market events found.</p>
          <p className="text-sm text-muted-foreground mt-1">
            Market dates are managed on the GMC website.
          </p>
        </CardContent>
      </Card>
    )
  }

  const getPayoutInfo = (eventId) => {
    const job = syncJobs[eventId]
    return {
      status: job?.payout_status || null,
      date: job?.payout_date || null,
      fee: job?.payout_fee ? parseFloat(job.payout_fee) : 0
    }
  }

  const formatPayoutDate = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    })
  }

  const renderEventRow = (event) => {
    const revenue = eventRevenue[event.id] || 0
    const cost = eventCosts[event.id] || 0
    const payout = getPayoutInfo(event.id)
    const totalCosts = cost + payout.fee
    const profit = revenue - totalCosts
    const isSyncing = syncingEventId === event.id

    return (
      <tr
        key={event.id}
        className="border-b last:border-0 hover:bg-muted/50 cursor-pointer transition-colors"
        onClick={() => navigate(`/sales/markets/${event.id}`)}
      >
        <td className="py-3 px-4">
          <div className="font-medium">{event.market_locations?.name || '—'}</div>
          <div className="text-xs text-muted-foreground">{event.market_locations?.address}</div>
        </td>
        <td className="py-3 px-4">{formatDate(event.date)}</td>
        <td className="py-3 px-4 text-muted-foreground">
          {formatTime(event.start_time)} – {formatTime(event.end_time)}
        </td>
        <td className="py-3 px-4">{getSyncBadge(event.id)}</td>
        <td className="py-3 px-4 text-right font-medium">
          {revenue > 0 ? `£${revenue.toFixed(2)}` : '—'}
        </td>
        <td className="py-3 px-4 text-right">
          {totalCosts > 0 ? `£${totalCosts.toFixed(2)}` : '—'}
        </td>
        <td className="py-3 px-4 text-right text-muted-foreground">
          {payout.fee > 0 ? `£${payout.fee.toFixed(2)}` : '—'}
        </td>
        <td className={`py-3 px-4 text-right font-medium ${profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {revenue > 0 || totalCosts > 0 ? `£${profit.toFixed(2)}` : '—'}
        </td>
        <td className="py-3 px-4 text-muted-foreground">
          {payout.status === 'PAID' ? formatPayoutDate(payout.date) : payout.status === 'SENT' ? 'Pending' : '—'}
        </td>
        <td className="py-3 px-4 text-right">
          {isConnected && (
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => handleSync(e, event.id)}
              disabled={isSyncing}
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`} />
            </Button>
          )}
        </td>
      </tr>
    )
  }

  return (
    <div className="space-y-6">
      {upcoming.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Upcoming</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-4 font-medium text-muted-foreground">Location</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Date</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Time</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Sync</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Revenue</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Costs</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Fees</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Profit</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Paid</th>
                    <th className="py-2 px-4 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {upcoming.map(renderEventRow)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {past.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Past Events</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2 px-4 font-medium text-muted-foreground">Location</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Date</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Time</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Sync</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Revenue</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Costs</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Fees</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground text-right">Profit</th>
                    <th className="py-2 px-4 font-medium text-muted-foreground">Paid</th>
                    <th className="py-2 px-4 w-12"></th>
                  </tr>
                </thead>
                <tbody>
                  {past.map(renderEventRow)}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
